import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, InputAdornment, MenuItem, Select,
  FormControl, InputLabel, Skeleton, IconButton, Chip, Tooltip,
  useTheme, useMediaQuery, Avatar, Badge, Collapse, Button,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Search, AlertCircle, X, Filter, Ticket, Building2, User,
  Zap, CheckCircle2, Clock, RefreshCw, ChevronRight,
} from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTickets } from '../../hooks/useTickets';
import { StatusBadge } from '../../components/StatusBadge';
import type { SupportTicketListDto, TicketStatus, TicketPriority } from '../../types/ticket';

dayjs.extend(relativeTime);

const STATUSES: TicketStatus[] = [
  'Open', 'InProgress', 'WaitingForCustomer', 'WaitingForThirdParty', 'Resolved', 'Closed', 'Reopened',
];
const PRIORITIES: TicketPriority[] = ['Critical', 'High', 'Normal', 'Low'];
const CATEGORIES = ['BugIssue', 'BillingSubscription', 'FeatureRequest', 'GeneralSupport'];
const CATEGORY_LABELS: Record<string, string> = {
  BugIssue: 'Bug / Issue',
  BillingSubscription: 'Billing',
  FeatureRequest: 'Feature Request',
  GeneralSupport: 'General Support',
};

const PRIORITY_CONFIG: Record<TicketPriority, { dot: string; color: string; bg: string; border: string }> = {
  Critical: { dot: '#EF4444', color: '#F87171', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.25)' },
  High:     { dot: '#F97316', color: '#FB923C', bg: 'rgba(249,115,22,0.12)', border: 'rgba(249,115,22,0.25)' },
  Normal:   { dot: '#3B82F6', color: '#60A5FA', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.25)' },
  Low:      { dot: '#64748B', color: '#94A3B8', bg: 'rgba(100,116,139,0.12)', border: 'rgba(100,116,139,0.2)' },
};

function PriorityDot({ priority }: { priority: TicketPriority }) {
  const cfg = PRIORITY_CONFIG[priority] ?? PRIORITY_CONFIG.Low;
  return (
    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: cfg.dot, boxShadow: `0 0 6px ${cfg.dot}99`, flexShrink: 0 }} />
  );
}

function PriorityBadge({ priority }: { priority: TicketPriority }) {
  const cfg = PRIORITY_CONFIG[priority] ?? PRIORITY_CONFIG.Low;
  return (
    <Chip
      label={priority}
      size="small"
      sx={{
        height: 22, fontSize: '0.68rem', fontWeight: 700,
        bgcolor: cfg.bg, color: cfg.color,
        border: `1px solid ${cfg.border}`, borderRadius: '6px',
        '& .MuiChip-label': { px: 0.875 },
      }}
    />
  );
}

// Mobile card for each ticket row
function TicketCard({ ticket, onClick }: { ticket: SupportTicketListDto; onClick: () => void }) {
  const breached = ticket.slaBreach && ticket.status !== 'Resolved' && ticket.status !== 'Closed';
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 1.5, mb: 1, borderRadius: '10px', cursor: 'pointer',
        border: '1px solid rgba(255,255,255,0.07)',
        bgcolor: 'rgba(255,255,255,0.02)',
        transition: 'all 0.15s ease',
        '&:hover': { borderColor: 'rgba(138,112,214,0.4)', bgcolor: 'rgba(138,112,214,0.06)', transform: 'translateX(2px)' },
        '&:active': { transform: 'translateX(1px)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 0.75 }}>
        <Box sx={{ pt: '3px', flexShrink: 0 }}>
          <PriorityDot priority={ticket.priority as TicketPriority} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', lineHeight: 1.3, mb: 0.2 }} noWrap>
            {ticket.subject}
          </Typography>
          <Typography variant="caption" sx={{ color: 'primary.light', fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: 700 }}>
            {ticket.ticketNumber}
          </Typography>
        </Box>
        <ChevronRight size={14} style={{ color: '#475569', flexShrink: 0, marginTop: 2 }} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <StatusBadge status={ticket.status as TicketStatus} />
        <PriorityBadge priority={ticket.priority as TicketPriority} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, ml: 'auto' }}>
          {breached && <AlertCircle size={11} color="#EF4444" />}
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
            {dayjs(ticket.createdOn).fromNow()}
          </Typography>
        </Box>
      </Box>
      {ticket.instituteName && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.5 }}>
          <Building2 size={10} style={{ color: '#475569' }} />
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
            {ticket.instituteName}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default function SupportTicketsPage() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const { useList, useStats } = useTickets();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [slaFilter, setSlaFilter] = useState<string>('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data: stats } = useStats();

  const { data, isLoading, refetch } = useList({
    pageNumber: page + 1,
    pageSize,
    status: statusFilter as TicketStatus || undefined,
    priority: priorityFilter as TicketPriority || undefined,
    category: categoryFilter || undefined,
    slaBreach: slaFilter === '' ? undefined : slaFilter === 'true',
  });

  const activeFilters = [statusFilter, priorityFilter, categoryFilter, slaFilter].filter(Boolean).length;

  const clearFilters = useCallback(() => {
    setStatusFilter(''); setPriorityFilter(''); setCategoryFilter(''); setSlaFilter(''); setPage(0);
  }, []);

  const goToTicket = (id: number) => navigate(`/support-tickets/${id}`);

  // ── DataGrid columns ──────────────────────────────────────────────────────
  const columns: GridColDef<SupportTicketListDto>[] = [
    {
      field: '_priority_dot',
      headerName: '',
      width: 24, sortable: false, disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <PriorityDot priority={row.priority as TicketPriority} />
        </Box>
      ),
    },
    {
      field: 'ticketNumber',
      headerName: 'Ticket #',
      width: 120,
      renderCell: ({ row }) => (
        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.73rem', color: 'primary.light', fontWeight: 700, letterSpacing: '0.02em' }}>
          {row.ticketNumber}
        </Typography>
      ),
    },
    {
      field: 'subject',
      headerName: 'Subject',
      flex: 2, minWidth: 200,
      renderCell: ({ row }) => (
        <Box sx={{ py: 0.5, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.subject}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Building2 size={9} style={{ color: '#475569', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.67rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {row.instituteName ?? '—'}  {row.reportedByName ? `· ${row.reportedByName}` : ''}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'module',
      headerName: 'Module',
      width: 100,
      renderCell: ({ row }) => (
        <Chip
          label={row.module ?? '—'}
          size="small"
          sx={{
            height: 20, fontSize: '0.67rem', fontWeight: 600,
            bgcolor: row.module ? 'rgba(59,130,246,0.1)' : 'transparent',
            color: row.module ? '#60A5FA' : 'text.disabled',
            border: row.module ? '1px solid rgba(59,130,246,0.2)' : 'none',
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 95,
      renderCell: ({ row }) => <PriorityBadge priority={row.priority as TicketPriority} />,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: ({ row }) => <StatusBadge status={row.status as TicketStatus} />,
    },
    {
      field: 'slaDeadline',
      headerName: 'SLA',
      width: 105,
      renderCell: ({ row }) => {
        const breached = row.slaBreach && row.status !== 'Resolved' && row.status !== 'Closed';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {breached && <AlertCircle size={11} color="#EF4444" />}
            <Typography sx={{ fontSize: '0.71rem', color: breached ? '#F87171' : 'text.secondary' }}>
              {row.slaDeadline ? dayjs(row.slaDeadline).format('DD MMM HH:mm') : '—'}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'assignedTo',
      headerName: 'Assigned',
      width: 120,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Avatar sx={{ width: 20, height: 20, fontSize: '0.52rem', fontWeight: 700, bgcolor: row.assignedTo ? 'rgba(138,112,214,0.3)' : 'rgba(100,116,139,0.2)', color: row.assignedTo ? '#A593E0' : '#475569' }}>
            {row.assignedTo?.[0]?.toUpperCase() ?? <User size={9} />}
          </Avatar>
          <Typography sx={{ fontSize: '0.73rem', color: row.assignedTo ? 'text.primary' : 'text.disabled' }} noWrap>
            {row.assignedTo ?? 'Unassigned'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'createdOn',
      headerName: 'Age',
      width: 85,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Clock size={10} style={{ color: '#475569' }} />
          <Typography sx={{ fontSize: '0.71rem', color: 'text.disabled' }}>
            {dayjs(row.createdOn).fromNow(true)}
          </Typography>
        </Box>
      ),
    },
    {
      field: '_action',
      headerName: '',
      width: 40, sortable: false, disableColumnMenu: true,
      renderCell: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <ChevronRight size={14} style={{ color: '#334155' }} />
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', minHeight: 0, gap: 1.5 }}>

      {/* ── Stats row ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)' }, gap: 1.25, flexShrink: 0 }}>
        {[
          { label: 'Open', value: stats?.open ?? 0, icon: <Ticket size={15} />, color: '#3B82F6', bg: 'rgba(59,130,246,0.08)', border: 'rgba(59,130,246,0.2)' },
          { label: 'In Progress', value: stats?.inProgress ?? 0, icon: <Zap size={15} />, color: '#F59E0B', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.2)' },
          { label: 'SLA Breached', value: stats?.slaBreached ?? 0, icon: <AlertCircle size={15} />, color: '#EF4444', bg: 'rgba(239,68,68,0.08)', border: 'rgba(239,68,68,0.2)' },
          { label: 'Resolved Today', value: stats?.resolvedToday ?? 0, icon: <CheckCircle2 size={15} />, color: '#10B981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)' },
        ].map(({ label, value, icon, color, bg, border }) => (
          <Paper key={label} elevation={0} sx={{ p: '10px 14px', border: '1px solid', borderColor: border, bgcolor: bg, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{ color, opacity: 0.85 }}>{icon}</Box>
            <Box>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color, lineHeight: 1, letterSpacing: '-0.02em' }}>{value}</Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.63rem' }}>{label}</Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', overflow: 'hidden', flexShrink: 0 }}>
        <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search tickets…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ width: { xs: '100%', sm: 220 } }}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start"><Search size={13} style={{ color: '#64748B' }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25 }}><X size={11} /></IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />

          {/* Quick status pills */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, flexWrap: 'wrap' }}>
            {[
              { label: 'All', value: '' },
              { label: 'Open', value: 'Open' },
              { label: 'In Progress', value: 'InProgress' },
              { label: 'Waiting', value: 'WaitingForCustomer' },
              { label: 'Resolved', value: 'Resolved' },
            ].map(({ label, value }) => (
              <Chip
                key={value || 'all'}
                label={label}
                size="small"
                onClick={() => { setStatusFilter(value); setPage(0); }}
                sx={{
                  height: 26, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer',
                  bgcolor: statusFilter === value ? 'rgba(138,112,214,0.2)' : 'rgba(255,255,255,0.04)',
                  color: statusFilter === value ? 'primary.light' : 'text.secondary',
                  border: '1px solid',
                  borderColor: statusFilter === value ? 'rgba(138,112,214,0.4)' : 'rgba(255,255,255,0.07)',
                  '&:hover': { bgcolor: 'rgba(138,112,214,0.1)', borderColor: 'rgba(138,112,214,0.3)' },
                  transition: 'all 0.15s',
                }}
              />
            ))}
          </Box>

          <Box sx={{ flex: 1 }} />

          {data && (
            <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>
              {data.totalCount.toLocaleString()} tickets
            </Typography>
          )}

          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetch()} sx={{ color: 'text.disabled', '&:hover': { color: 'text.primary' } }}>
              <RefreshCw size={13} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Filters">
            <IconButton
              size="small"
              onClick={() => setFiltersOpen(v => !v)}
              sx={{ color: activeFilters > 0 ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}
            >
              <Badge badgeContent={activeFilters} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', minWidth: 14, height: 14 } }}>
                <Filter size={13} />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ px: 1.5, pb: 1.25, pt: 0.75, borderTop: '1px solid rgba(255,255,255,0.05)', bgcolor: 'rgba(255,255,255,0.01)', display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Status</InputLabel>
              <Select label="Status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} sx={{ fontSize: '0.75rem' }}>
                <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All statuses</MenuItem>
                {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: '0.75rem' }}>{s}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 115 }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Priority</InputLabel>
              <Select label="Priority" value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setPage(0); }} sx={{ fontSize: '0.75rem' }}>
                <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                {PRIORITIES.map(p => <MenuItem key={p} value={p} sx={{ fontSize: '0.75rem' }}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>Category</InputLabel>
              <Select label="Category" value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(0); }} sx={{ fontSize: '0.75rem' }}>
                <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All categories</MenuItem>
                {CATEGORIES.map(c => <MenuItem key={c} value={c} sx={{ fontSize: '0.75rem' }}>{CATEGORY_LABELS[c]}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 105 }}>
              <InputLabel sx={{ fontSize: '0.75rem' }}>SLA</InputLabel>
              <Select label="SLA" value={slaFilter} onChange={(e) => { setSlaFilter(e.target.value); setPage(0); }} sx={{ fontSize: '0.75rem' }}>
                <MenuItem value="" sx={{ fontSize: '0.75rem' }}>All</MenuItem>
                <MenuItem value="true" sx={{ fontSize: '0.75rem' }}>Breached</MenuItem>
                <MenuItem value="false" sx={{ fontSize: '0.75rem' }}>On Track</MenuItem>
              </Select>
            </FormControl>
            {activeFilters > 0 && (
              <Button size="small" onClick={clearFilters} startIcon={<X size={11} />} sx={{ fontSize: '0.72rem', height: 30, color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                Clear filters
              </Button>
            )}
          </Box>
        </Collapse>
      </Paper>

      {/* ── Ticket list ───────────────────────────────────────────────── */}
      {isSmall ? (
        /* Mobile card list */
        <Box sx={{ flex: 1, overflowY: 'auto', px: 0.5 }}>
          {isLoading
            ? [...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={86} sx={{ mb: 1, borderRadius: '10px' }} />)
            : (data?.data ?? []).length === 0
            ? (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.disabled' }}>
                <Ticket size={32} style={{ opacity: 0.2, marginBottom: 12 }} />
                <Typography variant="body2">No tickets found</Typography>
              </Box>
            )
            : (data?.data ?? []).map(t => (
              <TicketCard key={t.id} ticket={t} onClick={() => goToTicket(t.id)} />
            ))
          }
        </Box>
      ) : (
        /* Desktop DataGrid — full width */
        <Paper elevation={0} sx={{ flex: 1, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', overflow: 'hidden', minHeight: 0 }}>
          {isLoading ? (
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {[...Array(10)].map((_, i) => <Skeleton key={i} variant="rounded" height={48} sx={{ borderRadius: '7px' }} />)}
            </Box>
          ) : (
            <DataGrid
              rows={data?.data ?? []}
              columns={columns}
              rowCount={data?.totalCount ?? 0}
              paginationMode="server"
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
              pageSizeOptions={[10, 25, 50, 100]}
              rowHeight={52}
              onRowClick={(params) => goToTicket(params.row.id)}
              disableRowSelectionOnClick
              sx={{
                border: 'none', height: '100%', cursor: 'pointer',
                '& .MuiDataGrid-columnHeader': {
                  bgcolor: 'rgba(255,255,255,0.025)',
                  fontSize: '0.67rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.disabled',
                },
                '& .MuiDataGrid-row': {
                  '&:hover': { bgcolor: 'rgba(138,112,214,0.07)' },
                  '&:hover .MuiSvgIcon-root': { color: '#8A70D6' },
                  transition: 'background 0.1s',
                },
                '& .MuiDataGrid-cell': { borderColor: 'rgba(255,255,255,0.035)', outline: 'none !important' },
                '& .MuiDataGrid-cell:focus': { outline: 'none' },
                '& .MuiDataGrid-columnSeparator': { display: 'none' },
                '& .MuiDataGrid-footerContainer': {
                  borderColor: 'rgba(255,255,255,0.06)',
                  bgcolor: 'rgba(255,255,255,0.01)',
                  minHeight: 44,
                },
                '& .MuiTablePagination-root': { fontSize: '0.75rem', color: 'text.secondary' },
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
              }}
            />
          )}
        </Paper>
      )}
    </Box>
  );
}
